<template>
  <div class="event-reg-form residence-form">
    <div class="row align-items-center">
      <section class="col-md-6 pr-lg-5">
        <p class="residence-info-text">
          Please enter the accurate residential address where you are physically
          residing and competing at the time of event participation.
        </p>

        <div class="input-field form-group">
          <vue-tel-input
            v-model="form.residencePhone"
            @input="validatePhone"
            @country-changed="countryChanged"
            class="form-control"
            :class="{
              'is-invalid': submitted && (getError('residencePhone') || !phone.valid),
            }"
          >
          </vue-tel-input>
          <label class="top-label">Contact Number<span class="text-danger">*</span></label>
          <div
            class="error-message"
            v-if="submitted && (getError('residencePhone') || !phone.valid)"
          >
            Please enter a valid contact number
          </div>
        </div>

        <div class="select-field form-group">
          <label>Country of Residence<span class="text-danger">*</span></label>
          <v-select
            class="style-chooser"
            label="name"
            :options="countriesData"
            v-model="selectedCountryOption"
            :clearable="false"
            placeholder="Select your current country"
          >
            <template #option="{ emoji, name }">
              {{ emoji }} {{ name }}
            </template>
            <template #selected-option="{ emoji, name }">
              {{ emoji }} {{ name }}
            </template>
          </v-select>
          <div v-if="submitted && getError('residenceCountry')" class="error-message">
            Please select your country of residence
          </div>
        </div>

        <div class="input-field form-group">
          <input
            v-model.trim="form.residenceState"
            class="form-control"
            type="text"
          />
          <label :class="form.residenceState ? 'top-label' : ''">State / Province<span class="text-danger">*</span></label>
          <div v-if="submitted && getError('residenceState')" class="error-message">
            required
          </div>
        </div>

        <div class="input-field form-group">
          <input
            v-model.trim="form.residenceCity"
            class="form-control"
            type="text"
          />
          <label :class="form.residenceCity ? 'top-label' : ''">City<span class="text-danger">*</span></label>
          <div v-if="submitted && getError('residenceCity')" class="error-message">
            required
          </div>
        </div>

        <div class="input-field form-group">
          <input
            v-model.trim="form.residenceAddress"
            class="form-control"
            type="text"
          />
          <label :class="form.residenceAddress ? 'top-label' : ''">Full Residential Address<span class="text-danger">*</span></label>
          <div v-if="submitted && getError('residenceAddress')" class="error-message">
            required
          </div>
        </div>

        <div class="input-field form-group">
          <input
            v-model.trim="form.residencePostalCode"
            class="form-control"
            type="text"
          />
          <label :class="form.residencePostalCode ? 'top-label' : ''">Postal / ZIP Code</label>
        </div>

        <div class="text-button-wrap" style="margin-left: 0px">
          <div class="custom-control custom-checkbox">
            <input
              id="residenceAgree"
              v-model="form.residenceAgree"
              class="custom-control-input"
              type="checkbox"
            />
            <label
              class="custom-control-label text-12 mt-3 terms-condition-label"
              for="residenceAgree"
            >
              By clicking Submit, I agree to the
              <a href="https://mywhoosh.com/ruleset/" target="_blank"
                >Terms and Conditions</a
              >
              and
              <a href="https://mywhoosh.com/privacy-policy/" target="_blank"
                >Privacy Policy</a
              >
            </label>
          </div>
          <small v-if="submitted && !form.residenceAgree" class="text-danger ml-4"
            >Please agree to the terms and conditions</small
          >
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
  numeric,
  sameAs,
} from "vuelidate/lib/validators";
import PhoneNumberService from "@/services/PhoneNumberService";

export default {
  name: "ResidenceForm",
  props: ["event", "user"],

  data() {
    return {
      submitted: false,
      phone: {},
      countriesData: [],
      selectedCountryOption: null,
      form: {
        residencePhoneCode: this.user?.country?.toString() || "971",
        residencePhone: this.user?.phone?.toString() || "",
        residenceCountry: null,
        residenceCountryName: null,
        residenceState: "",
        residenceCity: "",
        residenceAddress: "",
        residencePostalCode: "",
        residenceAgree: false,
      },
    };
  },

  validations: {
    form: {
      residencePhone: {
        required,
        numeric,
        minLength: minLength(6),
        maxLength: maxLength(15),
      },
      residencePhoneCode: { required },
      residenceCountry: { required },
      residenceState: { required },
      residenceCity: { required },
      residenceAddress: { required },
      residenceAgree: { sameAs: sameAs(() => true) },
    },
  },

  watch: {
    selectedCountryOption(option) {
      if (option) {
        this.form.residenceCountry = option.code;
        this.form.residenceCountryName = option.name;
      } else {
        this.form.residenceCountry = null;
        this.form.residenceCountryName = null;
      }
    },
  },

  mounted() {
    this.countriesData = PhoneNumberService.loadCountries();
  },

  methods: {
    countryChanged(country) {
      this.form.residencePhoneCode = country.dialCode;
    },

    validatePhone(number, phoneObj) {
      this.phone = phoneObj;
      this.form.residencePhone = phoneObj?.nationalNumber;
    },

    validate() {
      this.submitted = true;
      this.$v.form.$touch();
      const isValid = !this.$v.form.$invalid && this.phone.valid;
      this.$emit("on-validate", this.form, isValid);
      return isValid;
    },

    getError(field) {
      return this.$v.form[field].$error;
    },
  },
};
</script>

<style scoped>
.residence-info-text {
  font-size: 14px;
  color: #333;
  margin-bottom: 20px;
}

.custom-checkbox
  .custom-control-input:checked
  ~ .terms-condition-label::before {
  background-color: #fbb814 !important;
}
</style>

<style>
/* event-form.css does `@media (max-width:767px){ .input-field span{display:block;padding-top:5px} }`
   for helper-text spans. Inside this form that rule also hits every <span> in a
   floating <label> (the required `*`) and every <span> inside vue-tel-input
   (flag / dial-code / arrow), stacking them on top of the field on mobile.
   Restore their intended inline/flex layout below. */
.residence-form .input-field label span {
  display: inline;
  padding: 0;
  margin-left: 2px;
}

.residence-form .vue-tel-input span {
  display: inline;
  padding: 0;
}

.residence-form .vue-tel-input .vti__selection {
  display: flex;
  align-items: center;
}

.residence-form .vue-tel-input .vti__flag-wrapper,
.residence-form .vue-tel-input .vti__flag {
  display: inline-block;
}

.residence-form .input-field:has(.vue-tel-input) {
  position: relative;
  z-index: 9999;
}

.residence-form .vue-tel-input {
  position: relative;
  z-index: 9999;
}

.residence-form .vue-tel-input .vti__dropdown {
  position: static !important;
}

.residence-form .vue-tel-input .vti__dropdown-list {
  z-index: 9999 !important;
  background-color: #fff !important;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  border: 1px solid #ccc !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  max-height: 250px !important;
  overflow-y: auto !important;
}

.residence-form .vue-tel-input .vti__dropdown-item {
  background-color: #fff !important;
  padding: 6px 10px !important;
}

.residence-form .vue-tel-input .vti__dropdown-item:hover,
.residence-form .vue-tel-input .vti__dropdown-item.highlighted {
  background-color: #f3f3f3 !important;
}

.residence-form .select-field .vs__dropdown-toggle {
  min-height: 55px;
  padding: 8px 4px;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.residence-form .select-field .vs__selected-options {
  height: auto !important;
  min-height: 36px;
  align-items: center;
}

.residence-form .select-field .vs__selected {
  margin: 0 2px;
  padding: 4px 0;
  font-size: 16px;
  position: static;
}

.residence-form .select-field .vs__search,
.residence-form .select-field .vs__search:focus {
  padding: 4px 7px;
  margin: 0;
}

.residence-form .select-field .vs--single.vs--open .vs__selected {
  position: absolute;
  opacity: 0.4;
}
</style>
